import React, { useState, useEffect } from 'react';
import { Input, Button, Space, Select, Switch, message } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import {
  flattenProfilePreferences,
  nestProfilePreferences,
  normalizeProfileData,
} from '../../utils/normalizeProfileData';

const { Option } = Select;
const { TextArea } = Input;

type FieldType = 'string' | 'number' | 'boolean' | 'json';

interface ProfileDataItem {
  key: string;
  value: unknown;
  type: FieldType;
}

interface ProfileDataEditorProps {
  value?: unknown;
  onChange?: (value: Record<string, unknown>) => void;
}

function inferType(val: unknown): FieldType {
  if (typeof val === 'number') return 'number';
  if (typeof val === 'boolean') return 'boolean';
  if (val !== null && typeof val === 'object') return 'json';
  return 'string';
}

function toEditorItems(raw: unknown): ProfileDataItem[] {
  const normalized = flattenProfilePreferences(normalizeProfileData(raw));
  return Object.entries(normalized).map(([key, val]) => ({
    key,
    value: val,
    type: inferType(val),
  }));
}

export const ProfileDataEditor: React.FC<ProfileDataEditorProps> = ({
  value = {},
  onChange,
}) => {
  const [items, setItems] = useState<ProfileDataItem[]>([]);

  useEffect(() => {
    setItems(toEditorItems(value));
  }, [value]);

  const handleAddItem = () => {
    const newItems: ProfileDataItem[] = [
      ...items,
      { key: '', value: '', type: 'string' },
    ];
    setItems(newItems);
    notifyChange(newItems);
  };

  const handleRemoveItem = (index: number) => {
    const newItems = items.filter((_, i) => i !== index);
    setItems(newItems);
    notifyChange(newItems);
  };

  const handleKeyChange = (index: number, newKey: string) => {
    const duplicate = items.find(
      (item, i) => i !== index && item.key === newKey && item.key !== ''
    );
    if (duplicate) {
      message.warning('Key already exists');
      return;
    }

    const newItems = [...items];
    newItems[index] = { ...newItems[index], key: newKey };
    setItems(newItems);
    notifyChange(newItems);
  };

  const handleValueChange = (index: number, newValue: unknown) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], value: newValue };
    setItems(newItems);
    notifyChange(newItems);
  };

  const handleTypeChange = (index: number, newType: FieldType) => {
    const newItems = [...items];
    const current = newItems[index];
    let nextValue: unknown = current.value;

    if (newType === 'boolean') {
      nextValue = current.value === true || current.value === 'true';
    } else if (newType === 'number') {
      const numValue = Number(current.value);
      nextValue = Number.isNaN(numValue) ? 0 : numValue;
    } else if (newType === 'json') {
      if (typeof current.value === 'string') {
        try {
          nextValue = JSON.parse(current.value);
        } catch {
          nextValue = {};
        }
      } else if (current.value === null || typeof current.value !== 'object') {
        nextValue = {};
      }
    } else if (current.type === 'json') {
      nextValue = JSON.stringify(current.value ?? {}, null, 2);
    } else {
      nextValue = String(current.value ?? '');
    }

    newItems[index] = { ...current, type: newType, value: nextValue };
    setItems(newItems);
    notifyChange(newItems);
  };

  const notifyChange = (itemsToConvert: ProfileDataItem[]) => {
    if (!onChange) return;

    const flat: Record<string, unknown> = {};
    itemsToConvert.forEach((item) => {
      if (item.key.trim() === '') return;
      if (item.type === 'json' && typeof item.value === 'string') {
        try {
          flat[item.key] = JSON.parse(item.value);
        } catch {
          flat[item.key] = item.value;
        }
      } else {
        flat[item.key] = item.value;
      }
    });

    onChange(nestProfilePreferences(flat));
  };

  const jsonText = (val: unknown): string => {
    if (typeof val === 'string') return val;
    try {
      return JSON.stringify(val ?? {}, null, 2);
    } catch {
      return '';
    }
  };

  return (
    <div data-testid="profile-data-editor">
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        {items.map((item, index) => (
          <Space
            key={`${item.key}-${index}`}
            style={{ width: '100%' }}
            align="start"
            wrap
          >
            <Input
              placeholder="Key"
              value={item.key}
              onChange={(e) => handleKeyChange(index, e.target.value)}
              style={{ width: 180 }}
            />
            <Select
              value={item.type}
              onChange={(val: FieldType) => handleTypeChange(index, val)}
              style={{ width: 110 }}
            >
              <Option value="string">String</Option>
              <Option value="number">Number</Option>
              <Option value="boolean">Boolean</Option>
              <Option value="json">JSON</Option>
            </Select>
            {item.type === 'boolean' ? (
              <Switch
                checked={item.value === true || item.value === 'true'}
                onChange={(checked) => handleValueChange(index, checked)}
              />
            ) : item.type === 'number' ? (
              <Input
                type="number"
                placeholder="Value"
                value={item.value as number | string}
                onChange={(e) => handleValueChange(index, Number(e.target.value))}
                style={{ minWidth: 200, flex: 1 }}
              />
            ) : item.type === 'json' ? (
              <TextArea
                placeholder="{}"
                value={jsonText(item.value)}
                onChange={(e) => {
                  const text = e.target.value;
                  try {
                    handleValueChange(index, JSON.parse(text));
                  } catch {
                    handleValueChange(index, text);
                  }
                }}
                autoSize={{ minRows: 2, maxRows: 8 }}
                style={{ minWidth: 280, flex: 1 }}
              />
            ) : (
              <Input
                placeholder="Value"
                value={String(item.value ?? '')}
                onChange={(e) => handleValueChange(index, e.target.value)}
                style={{ minWidth: 200, flex: 1 }}
              />
            )}
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleRemoveItem(index)}
            />
          </Space>
        ))}
        <Button type="dashed" icon={<PlusOutlined />} onClick={handleAddItem} block>
          Add Field
        </Button>
      </Space>
    </div>
  );
};
